import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import { Package, PackagePlus, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import RailTooltip from './RailTooltip';
import { navItemClass } from './NavItem';

const OPTIONS = [
  { to: '/shipments/new', icon: PackagePlus, label: 'New Shipment', hint: 'Calculate one shipment' },
  { to: '/shipments/bulk', icon: Upload, label: 'Bulk Upload', hint: 'Excel or CSV, many rows' },
];

/**
 * Collapsed-rail Shipments entry: one icon that opens a small menu beside it with New Shipment and
 * Bulk Upload, so nobody has to expand the sidebar just to choose. The menu grows out of the icon
 * (transform-origin = the trigger, set by Radix) and closes on choice, Esc or an outside click;
 * Radix handles focus and arrow-key navigation.
 */
export default function ShipmentsRailMenu({ onItemClick }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const active = pathname.startsWith('/shipments');

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <RailTooltip label="Shipments" disabled={open}>
        <Popover.Trigger asChild>
          <button
            type="button"
            aria-label="Shipments"
            aria-haspopup="menu"
            className={cn(navItemClass({ isActive: active, collapsed: true }), open && !active && 'bg-secondary text-foreground')}
          >
            <Package className={cn('h-5 w-5 flex-shrink-0', active && 'text-primary')} aria-hidden="true" />
          </button>
        </Popover.Trigger>
      </RailTooltip>
      <Popover.Portal>
        <Popover.Content side="right" align="start" sideOffset={12} collisionPadding={12} className="rail-popover">
          <p className="px-2.5 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Shipments
          </p>
          <div role="menu" aria-label="Shipments" className="space-y-0.5">
            {OPTIONS.map(({ to, icon: Icon, label, hint }) => (
              <NavLink
                key={to}
                to={to}
                role="menuitem"
                onClick={() => { setOpen(false); onItemClick?.(); }}
                className={({ isActive }) => cn('rail-popover__item', isActive && 'rail-popover__item--active')}
              >
                {({ isActive }) => (
                  <>
                    <span className={cn('rail-popover__icon', isActive && 'text-primary')}>
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground">{label}</span>
                      <span className="block text-xs text-muted-foreground">{hint}</span>
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
